import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTopics } from './queries/useTopics';
import { logger } from '../utils/logger';
import { topicKeys } from './queries/topicKeys';
import { useLocation } from 'react-router-dom';
import { useViewport } from '../contexts/ViewportContext';
import { useTopicUIStore } from '../store/topicUIStore';
import { FormattedTopic } from './queries/topicTypes';

/**
 * Hook to manage topic fetching when the active project changes using React Query
 *
 * This hook handles these cases:
 * 1. Initial load - automatic through React Query
 * 2. Project change - automatic through React Query's queryKey changes
 * 3. Manual refetch - provided if needed but rarely necessary with React Query
 * 4. Auto-selection of the most recently used topic when project changes
 *
 * @param projectId The currently active project ID
 * @param onTopicSelect Optional callback for topic auto-selection
 * @returns Object with topics data and loading state
 */
export function useTopicFetching(
  projectId: string | null,
  onTopicSelect?: (topicId: string) => void
) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const { isMobile } = useViewport();
  const { activeTopicId } = useTopicUIStore();

  // State to track topics for the current project only
  const [validatedTopics, setValidatedTopics] = useState<FormattedTopic[]>([]);

  // Add state to track if we're in a project transition (critical for preventing empty state flash)
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Reference to track if we've attempted auto-navigation for each project
  // We clear this state in specific scenarios
  const navigationAttemptsRef = useRef<Record<string, boolean>>({});

  // Track the current auto-navigation status for detailed logging
  const navigationStatusRef = useRef<{
    project: string | null;
    isEligible: boolean;
    reason?: string;
    lastAttempt?: number;
    wasSuccessful?: boolean;
  }>({
    project: null,
    isEligible: false,
  });

  // Track when a project change occurs and other key states
  const stateRef = useRef<{
    prevProjectId: string | null;
    prevPathname: string;
    lastValidatedAt: number;
    pathChangeCount: number;
    topicsReceived: boolean;
    projectSwitchCount: number;
  }>({
    prevProjectId: null,
    prevPathname: location.pathname,
    lastValidatedAt: 0,
    pathChangeCount: 0,
    topicsReceived: false,
    projectSwitchCount: 0,
  });

  // Use React Query's useTopics hook to fetch topics
  const {
    data: topics = [],
    isLoading,
    isFetching,
    isRefetching,
    dataUpdatedAt,
  } = useTopics(projectId, activeTopicId);

  // Forced navigation function - can be called directly if needed
  const forceTopicNavigation = useCallback(
    (targetTopicId: string) => {
      if (!projectId || !onTopicSelect) return false;

      // Set navigation status
      navigationStatusRef.current = {
        project: projectId,
        isEligible: true,
        lastAttempt: Date.now(),
        wasSuccessful: true,
        reason: 'FORCED_NAVIGATION',
      };

      // Mark project as navigated
      navigationAttemptsRef.current[projectId] = true;

      // Execute navigation
      onTopicSelect(targetTopicId);
      return true;
    },
    [projectId, onTopicSelect, location.pathname]
  );

  // Track projects we've already fetched data for
  // This helps us avoid showing skeletons for already-loaded projects
  const projectCacheRef = useRef<Record<string, boolean>>({});

  // IMPORTANT: When project changes, reset navigation state while preserving cache
  useEffect(() => {
    // Only take action on actual project changes
    if (projectId !== stateRef.current.prevProjectId) {
      // Set transition state immediately when project changes
      setIsTransitioning(true);

      // Increment project switch counter
      stateRef.current.projectSwitchCount++;

      // Reset navigation but not cache when project changes
      if (projectId) {
        // Reset navigation attempts to ensure we'll try auto-selection
        navigationAttemptsRef.current[projectId] = false;

        // Reset topics received flag to trigger revalidation
        stateRef.current.topicsReceived = false;

        // Update navigation status
        navigationStatusRef.current = {
          project: projectId,
          isEligible: false,
          reason: 'PROJECT_JUST_CHANGED',
        };

        // IMPORTANT FIX: For cached projects, we use cached data, but
        // we ALWAYS ensure we have correct topics for current project
        if (!projectCacheRef.current[projectId]) {
          // For new projects, always start with empty topics
          setValidatedTopics([]);
        } else {
          // For cached projects, check that our current topics are for this project
          // This fixes a race condition where topics from previous project might remain
          const allTopicsMatchProject = validatedTopics.every(t => t.projectId === projectId);

          if (!allTopicsMatchProject) {
            // Filter only topics that belong to this project
            const filteredTopics = validatedTopics.filter(t => t.projectId === projectId);

            if (filteredTopics.length > 0) {
              // If we have valid topics for this project, keep only those
              setValidatedTopics(filteredTopics);
            } else {
              // Otherwise clear completely to trigger proper loading
              setValidatedTopics([]);
            }
          }
        }
      }

      // Update previous project
      stateRef.current.prevProjectId = projectId;
    }
  }, [projectId, activeTopicId, location.pathname]);

  // Track path changes separately with a counter for debugging
  useEffect(() => {
    if (location.pathname !== stateRef.current.prevPathname) {
      stateRef.current.pathChangeCount++;
      stateRef.current.prevPathname = location.pathname;
    }
  }, [location.pathname, projectId, activeTopicId, validatedTopics]);

  // Validate topics when topics or project changes
  useEffect(() => {
    if (!projectId) {
      return;
    }

    // If we have an empty array of topics but we're not loading,
    // this means the project truly has no topics, so we should end the transition state
    if (topics.length === 0 && !isLoading && !isFetching && isTransitioning) {
      setIsTransitioning(false);
      return;
    }

    // If we have no topics but are still loading, keep waiting
    if (topics.length === 0) {
      return;
    }

    // Check if ALL topics belong to the current project
    const topicsMatchProject = topics.every(topic => topic.projectId === projectId);

    if (topicsMatchProject) {
      // Mark that we've received valid topics for this project
      stateRef.current.topicsReceived = true;
      stateRef.current.lastValidatedAt = Date.now();

      // IMPORTANT: Mark this project as cached to avoid skeleton on revisit
      if (!projectCacheRef.current[projectId]) {
        projectCacheRef.current[projectId] = true;
      }

      // Update validated topics
      setValidatedTopics(topics);

      // CRITICAL FIX: End the transition state now that topics are validated
      // This will prevent empty state flashing by ensuring we keep showing loading state
      // until topics are fully validated
      if (isTransitioning) {
        setIsTransitioning(false);
      }
    }
  }, [
    projectId,
    topics,
    isLoading,
    location.pathname,
    activeTopicId,
    dataUpdatedAt,
    isRefetching,
    isFetching,
    isTransitioning,
  ]);

  // Auto-selection effect - make it more robust to handle all cases
  useEffect(() => {
    // Skip if we can't navigate
    if (!projectId || !onTopicSelect) {
      return;
    }

    // We need validated topics to navigate
    if (validatedTopics.length === 0) {
      navigationStatusRef.current = {
        project: projectId,
        isEligible: false,
        reason: 'NO_VALIDATED_TOPICS',
      };
      return;
    }

    // Check if we're at the project root path - this is critical
    const atProjectRoot =
      location.pathname === `/project/${projectId}` ||
      location.pathname === `/project/${projectId}/`;

    if (!atProjectRoot) {
      navigationStatusRef.current = {
        project: projectId,
        isEligible: false,
        reason: 'NOT_AT_PROJECT_ROOT',
      };
      return;
    }

    // Skip if this is a mobile device (we want to show topic list)
    if (isMobile) {
      navigationStatusRef.current = {
        project: projectId,
        isEligible: false,
        reason: 'MOBILE_DEVICE',
      };
      return;
    }

    // Skip if we already have an active topic
    if (activeTopicId) {
      navigationStatusRef.current = {
        project: projectId,
        isEligible: false,
        reason: 'ALREADY_HAS_ACTIVE_TOPIC',
      };
      return;
    }

    // Check if we've already attempted navigation for this project
    const hasAttemptedNavigation = navigationAttemptsRef.current[projectId] || false;

    if (hasAttemptedNavigation) {
      navigationStatusRef.current = {
        project: projectId,
        isEligible: false,
        reason: 'ALREADY_ATTEMPTED_NAVIGATION',
      };
      return;
    }

    // CRITICAL IMPROVEMENT: One final safety check to verify project data integrity
    const allTopicsMatchProject = validatedTopics.every(topic => topic.projectId === projectId);
    if (!allTopicsMatchProject) {
      navigationStatusRef.current = {
        project: projectId,
        isEligible: false,
        reason: 'TOPIC_PROJECT_MISMATCH',
      };

      return;
    }

    // At this point, we're eligible for auto-navigation
    navigationStatusRef.current = {
      project: projectId,
      isEligible: true,
      lastAttempt: Date.now(),
    };

    // Get most recently used topic
    const mostRecentTopic = validatedTopics[0];

    // Mark as navigated to prevent duplicate navigation
    navigationAttemptsRef.current[projectId] = true;

    // Update navigation status
    navigationStatusRef.current.wasSuccessful = true;

    // Execute navigation
    onTopicSelect(mostRecentTopic.id);
  }, [projectId, validatedTopics, location.pathname, activeTopicId, isMobile, onTopicSelect]);

  // Debugging effect - periodically log the current navigation state
  useEffect(() => {
    if (!projectId) return;
  }, [projectId, validatedTopics.length, activeTopicId, location.pathname]);

  // Manual refresh function
  const refreshTopics = async () => {
    if (!projectId) return;

    logger.refresh(`Manually refreshing topics for project: ${projectId}`);

    try {
      await queryClient.invalidateQueries({
        queryKey: topicKeys.list({ projectId }),
      });

      logger.refresh(`Manual topic refresh completed for project: ${projectId}`);
    } catch (error) {
      logger.error('Error in manual topic refresh:', error);
    }
  };

  return {
    // Return validated topics when available, otherwise empty array
    topics: validatedTopics,
    loading: isLoading,
    fetching: isFetching,
    isEmpty: validatedTopics.length === 0,
    refreshTopics,
    forceTopicNavigation,
    hasAutoNavigated: projectId ? navigationAttemptsRef.current[projectId] || false : false,
    navigationStatus: navigationStatusRef.current,
    // Add the hasProjectCache property to indicate if we've already loaded this project before
    hasProjectCache: projectId ? projectCacheRef.current[projectId] || false : false,
    // CRITICAL FIX: Export the transition state to prevent empty state flashing
    isTransitioning,
  };
}
